import { FC, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Progress, TextArea } from '@agro-club/agroclub-shared'

import useFormatDate from 'hooks/useFormatDate'
import { CommentType } from 'modules/domain/comment/types'
import { FooterControls } from 'views/components/Comments/FooterControls'
import { ACommentsTrack } from './AComments'
import { CommentsList } from './CommentsList'
import { useComments } from './hooks'
import * as S from './styled'
import AAntdTabs from '../Analytics/AAntdTabs'

export type InternalCommentsProps = {
  url: string
  id: string | number
  type?: CommentType
  isClosed?: boolean
  maxHeight?: number
  onSuccess?: () => void
  onATrack: ACommentsTrack
  popSummary?: (v: string) => void
}

export const InternalComments: FC<InternalCommentsProps> = ({
  url,
  isClosed,
  popSummary,
  onSuccess,
  onATrack,
  ...props
}) => {
  const { t } = useTranslation('comments')
  const [comment, setComment] = useState('')
  const [commentId, setCommentId] = useState('')
  const formatDate = useFormatDate()
  const [isEdit, setIsEdit] = useState(false)
  const [activeTab, setActiveTab] = useState<string>('common')

  const {
    commonComments,
    getCommonProgress,
    systemComments,
    getSystemProgress,
    addComment,
    sendProgress,
    editPinnedComment,
    setIsLoadSystem,
    unpinComment,
    unpinProgress,
  } = useComments(
    url,
    () => {
      onSuccess?.()
      setComment('')
    },

    commentId,
  )

  const onTabClick = (tabId) => {
    setActiveTab(tabId)
    if (tabId === 'system') {
      setIsLoadSystem(true)
    }
  }

  const hasPinned = useMemo(() => commonComments.some((comment) => comment.is_pinned), [commonComments])

  useEffect(() => {
    if (popSummary && commonComments?.length) {
      popSummary(formatDate(commonComments.slice(-1).pop()?.created, { withTime: true }))
    }
  }, [commonComments, formatDate, popSummary])

  const items = [
    {
      label: t('users'),
      key: 'common',
      children: (
        <>
          <CommentsList
            onATrack={onATrack}
            setCommentId={setCommentId}
            setComment={setComment}
            setIsEdit={setIsEdit}
            isEdit={isEdit}
            maxHeight={props.maxHeight}
            isLoading={getCommonProgress === Progress.WORK}
            comments={commonComments}
            unpinComment={unpinComment}
            unpinProgress={unpinProgress}
          />
          {!isClosed && (
            <div>
              <TextArea
                height={80}
                placeholder={t('enter_text')}
                value={comment}
                onChange={(e) => setComment(e.target.value)}
              />
            </div>
          )}
          <FooterControls
            onATrack={onATrack}
            addComment={addComment}
            comment={comment}
            isEdit={isEdit}
            sendProgress={sendProgress}
            setIsEdit={setIsEdit}
            setComment={setComment}
            editPinnedComment={editPinnedComment}
            hasPinned={hasPinned}
          />
        </>
      ),
    },
    {
      label: t('system'),
      key: 'system',
      children: (
        <CommentsList
          onATrack={onATrack}
          setCommentId={setCommentId}
          setComment={setComment}
          setIsEdit={setIsEdit}
          isEdit={isEdit}
          maxHeight={props.maxHeight}
          isLoading={getSystemProgress === Progress.WORK}
          comments={systemComments}
        />
      ),
    },
  ]
  return (
    <S.CommentWrapper>
      <AAntdTabs items={items} onChange={onTabClick} activeKey={activeTab} id="commentsTabClick" />
    </S.CommentWrapper>
  )
}
